import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { Spin } from 'antd';
import { useThemeVarsClass, Select } from '@bettercomp/ui-components';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from 'redux/user/selectors';
import { loadUserData } from 'redux/user';
import type { TypedDispatch } from 'redux/configureStore';
import { useCanSwitchTenancy } from 'hooks/useCurrentUser';
import { useTenantsLazy } from 'hooks/useTenants';
import { useTenantActions } from 'hooks/useTenantActions';
import { reportError } from 'utils/reporting';
import { createTenancyBroadcastChannel, TenancyBroadcastMessageType } from './constants';
import cn from './styles.module.scss';

const RETURN_HOME_VALUE = 'returnHome';

export const LegacyTenancySwitcher = () => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch<TypedDispatch>();
  const { canSwitchTenancy } = useCanSwitchTenancy();
  const { availableTenants, isLoading, loadTenants } = useTenantsLazy();
  const { switchTenant, returnToHomeTenant } = useTenantActions();
  const themeClassName = useThemeVarsClass();

  const [hasOpened, setHasOpened] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const channel = useMemo(() => canSwitchTenancy ? createTenancyBroadcastChannel() : null, [canSwitchTenancy]);

  useEffect(() => {
    if (!channel) {
      return;
    }

    const tenancyChannelMessageHandler = (message: { data: TenancyBroadcastMessageType }) => {
      if (message.data === TenancyBroadcastMessageType.TenancySwitched) {
        window.location.reload();
      }
    };

    channel.addEventListener('message', tenancyChannelMessageHandler);

    return () => {
      channel.removeEventListener('message', tenancyChannelMessageHandler);
      channel.close();
    };
  }, [channel]);

  const options = useMemo(() => {
    const currentTenant = availableTenants.find((tenant) => tenant.is_current);
    const homeTenant = availableTenants.find((tenant) => tenant.is_home);
    const isAwayFromHome = currentTenant && !currentTenant.is_home && homeTenant;

    const tenantOptions = availableTenants
      .filter((tenant) => !tenant.is_current)
      .map((tenant) => ({
        label: `${tenant.name}${tenant.is_home ? ' (Home)' : ''}`,
        value: tenant.tid,
      }));

    if (isAwayFromHome) {
      return [
        { label: '\u{1F3E0} Return to Home', value: RETURN_HOME_VALUE },
        ...tenantOptions,
      ];
    }

    return tenantOptions;
  }, [availableTenants]);

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && !hasOpened) {
      setHasOpened(true);
      loadTenants();
    }
  };

  const handleTenantChange = async (value: string) => {
    setIsSwitching(true);

    try {
      const result = value === RETURN_HOME_VALUE
        ? await returnToHomeTenant()
        : await switchTenant(value);

      if (!result) {
        setIsSwitching(false);
        return;
      }

      await dispatch(loadUserData({ login: true }));

      const broadcastChannel = createTenancyBroadcastChannel();
      broadcastChannel.postMessage(TenancyBroadcastMessageType.TenancySwitched);
    } catch (error) {
      reportError(error);
      setIsSwitching(false);
    } finally {
      setIsSwitching(false);
    }
  };

  // Allow both Super Admins and Tenant Admins to see the bar
  if (!canSwitchTenancy) {
    return null;
  }

  return (
    <>
      <div className={cn.wrapper}>
        <Select
          placeholder={user?.Company?.name}
          value={undefined}
          options={options}
          onChange={handleTenantChange}
          onOpenChange={handleDropdownVisibleChange}
          loading={isLoading}
          showSearch={{
            optionFilterProp: "label"
          }}
          variant="filled"
          size="medium"
          className={cn.tenantSelect}
          classNames={{
            popup: {
              root: cn.tenantSelectPopup
            }
          }}
          disabled={isSwitching}
          notFoundContent={isLoading ? <Spin size="small" /> : undefined}
          data-testid="tenantSelector"
        />
      </div>
      {isSwitching && createPortal(
        <div className={classnames(themeClassName, cn.switchOverlay)} data-testid="tenantSwitchOverlay">
          <Spin size="large" />
        </div>,
        document.body,
      )}
    </>
  );
};
